import { WebappApplicationFragment, useUpdateApplicationMutation } from '@gql'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from '@/components/ui/use-toast'
import { Button } from '@/components/ui/button'
import { InputDatePicker } from '@/components/ui/InputDatePicker'
import { FileUploadInput } from '@/common/atoms/FileUploadInput'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
} from '@/components/ui/form'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

type PromiseOfSaleFormProps = {
  application: WebappApplicationFragment
}

// Promise of Sale form schema and type
const promiseOfSaleSchema = yup.object().shape({
  signatureDate: yup.date().required('La date de signature est requise'),
  fileId: yup.string().required('Le document est requis'),
})

type PromiseOfSaleFormData = yup.InferType<typeof promiseOfSaleSchema>

export const PromiseOfSaleForm = ({ application }: PromiseOfSaleFormProps) => {
  const { t } = useTranslation()

  const [updateApplication] = useUpdateApplicationMutation({
    refetchQueries: ['applications'],
  })

  // Promise of Sale form
  const promiseOfSaleForm = useForm<PromiseOfSaleFormData>({
    resolver: yupResolver(promiseOfSaleSchema),
    defaultValues: {
      signatureDate: application.promiseOfSaleSignatureDate
        ? new Date(application.promiseOfSaleSignatureDate)
        : undefined,
      fileId: application.promiseOfSalesFileId || '',
    },
  })

  const onPromiseOfSaleSubmit = promiseOfSaleForm.handleSubmit(
    async (data: PromiseOfSaleFormData) => {
      if (!application || application.archived) return

      try {
        await updateApplication({
          variables: {
            id: application.id,
            values: {
              promiseOfSaleSignatureDate: data.signatureDate.toISOString(),
              promiseOfSalesFileId: data.fileId,
            },
          },
        })
        toast({
          title: t('ApplicationSheet.promiseOfSale.success.title'),
          description: t('ApplicationSheet.promiseOfSale.success.description'),
          variant: 'success',
        })
      } catch (error) {
        console.error(error)
        toast({
          title: t('ApplicationSheet.promiseOfSale.error.title'),
          description: t('ApplicationSheet.promiseOfSale.error.description'),
          variant: 'destructive',
        })
      }
    }
  )

  return (
    <div className="tw-border tw-rounded-lg tw-p-4 tw-space-y-4">
      <h3 className="tw-text-lg tw-font-semibold">
        {t('ApplicationSheet.promiseOfSale.title')}
      </h3>
      <Form {...promiseOfSaleForm}>
        <form onSubmit={onPromiseOfSaleSubmit} className="tw-space-y-4">
          <FormField
            control={promiseOfSaleForm.control}
            name="signatureDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('ApplicationSheet.promiseOfSale.signatureDate')}
                </FormLabel>
                <FormControl>
                  <InputDatePicker
                    value={field.value}
                    onChange={field.onChange}
                    className="tw-w-full"
                    placeholder={t(
                      'ApplicationSheet.promiseOfSale.datePlaceholder'
                    )}
                    fromYear={2020}
                    toYear={2030}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={promiseOfSaleForm.control}
            name="fileId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('ApplicationSheet.promiseOfSale.file')}
                </FormLabel>
                <FormControl>
                  <FileUploadInput
                    value={field.value}
                    onChange={field.onChange}
                    storageName="promise-of-sale"
                    accept=".pdf,.doc,.docx"
                    className="tw-w-full"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <Button type="submit" disabled={!promiseOfSaleForm.formState.isValid}>
            {t('ApplicationSheet.promiseOfSale.save')}
          </Button>
        </form>
      </Form>
    </div>
  )
}
